import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Box, Flex, Grid, Input, VStack } from '@chakra-ui/react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  LuCheck,
  LuClock,
  LuTriangleAlert,
  LuX,
  LuChevronDown,
  LuArrowLeft,
} from 'react-icons/lu'
import { toaster, Text, useT } from '@minetrace/ui'
import { brand, themeColors } from '../../theme'
import { recepcionesService } from '../../api/services/recepciones.service'
import {
  ResultadoValidacion,
  TipoValidacion,
  type ResultadoRecepcionResponse,
  type ResultadoValidacionItem,
  type ActualizarManualRequest,
} from '../../api/models/recepcion.models'

const MotionBox = motion.create(Box)

const RESULTADO_CONFIG: Record<
  ResultadoValidacion,
  { icon: typeof LuCheck; color: string; tid: string }
> = {
  [ResultadoValidacion.Conforme]: {
    icon: LuCheck,
    color: '#2a8a87',
    tid: 'recepciones.resultado.conforme',
  },
  [ResultadoValidacion.Observado]: {
    icon: LuTriangleAlert,
    color: '#EAB308',
    tid: 'recepciones.resultado.observado',
  },
  [ResultadoValidacion.NoConforme]: {
    icon: LuX,
    color: '#c8442f',
    tid: 'recepciones.resultado.noConforme',
  },
  [ResultadoValidacion.Pendiente]: {
    icon: LuClock,
    color: '#b5aa9d',
    tid: 'recepciones.resultado.pendiente',
  },
}

export default function ResultadoValidacionPage() {
  const { id: recepcionId } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const colors = themeColors
  const { t } = useT()

  const [resultado, setResultado] = useState<ResultadoRecepcionResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [showConfirmDialog, setShowConfirmDialog] = useState<'aprobar' | 'rechazar' | null>(null)
  const [observaciones, setObservaciones] = useState('')
  const [finalizando, setFinalizando] = useState(false)

  // Manual validation state
  const [manualForm, setManualForm] = useState<{
    validacionId: string
    resultado: ResultadoValidacion
    observaciones: string
  } | null>(null)
  const [savingManual, setSavingManual] = useState(false)

  useEffect(() => {
    if (!recepcionId) return
    const fetchResultado = async () => {
      setLoading(true)
      try {
        const data = await recepcionesService.getResultado(recepcionId)
        setResultado(data)
      } catch {
        // Error handled by service
      } finally {
        setLoading(false)
      }
    }
    fetchResultado()
  }, [recepcionId])

  const handleSaveManual = async () => {
    if (!recepcionId || !manualForm) return
    setSavingManual(true)
    try {
      const data: ActualizarManualRequest = {
        resultado: manualForm.resultado,
        observaciones: manualForm.observaciones,
      }
      await recepcionesService.actualizarManual(recepcionId, manualForm.validacionId, data)
      toaster.success({ description: t('recepciones.resultadoPage.toasts.manualUpdated') })
      // Refresh
      const updated = await recepcionesService.getResultado(recepcionId)
      setResultado(updated)
      setManualForm(null)
    } catch {
      // Error handled by service
    } finally {
      setSavingManual(false)
    }
  }

  const handleFinalizar = async (aprobada: boolean) => {
    if (!recepcionId) return
    setFinalizando(true)
    try {
      await recepcionesService.finalizar(recepcionId, {
        aprobada,
        observaciones,
      })
      toaster.success({
        description: aprobada
          ? t('recepciones.resultadoPage.toasts.aprobadaOk')
          : t('recepciones.resultadoPage.toasts.rechazadaOk'),
      })
      navigate('/app/recepciones')
    } catch {
      // Error handled by service
    } finally {
      setFinalizando(false)
      setShowConfirmDialog(null)
    }
  }

  if (loading) {
    return (
      <VStack gap={4} py={20}>
        <Text
          tid="recepciones.resultadoPage.loadingMessage"
          color={colors.textMuted}
          fontSize="sm"
        />
      </VStack>
    )
  }

  if (!resultado) {
    return (
      <VStack gap={4} py={20}>
        <Text tid="recepciones.resultadoPage.notFound" color={colors.text} fontSize="lg" />
        <Box
          as="button"
          color={brand.green}
          fontSize="sm"
          cursor="pointer"
          _hover={{ textDecoration: 'underline' }}
          onClick={() => navigate('/app/recepciones')}
        >
          {t('recepciones.actions.volverRecepciones')}
        </Box>
      </VStack>
    )
  }

  const resultadoGeneral = RESULTADO_CONFIG[resultado.resultado]
  const hasNoConformes = resultado.totalNoConformes > 0
  const hasPendientes = resultado.totalPendientes > 0
  const ResultIcon = resultadoGeneral.icon

  return (
    <VStack gap={6} alignItems="stretch" maxW="800px" mx="auto">
      {/* Back */}
      <Flex alignItems="center" gap={3}>
        <Box
          as="button"
          p={2}
          borderRadius="xl"
          cursor="pointer"
          color={colors.textMuted}
          _hover={{ bg: colors.bgHover, color: colors.text }}
          transition="all 0.2s"
          onClick={() => navigate('/app/recepciones')}
        >
          <LuArrowLeft size={20} />
        </Box>
        <Text
          tid="recepciones.resultadoPage.headerTitle"
          values={{ codigo: resultado.codigoLote }}
          fontSize="sm"
          color={colors.text}
          fontWeight="600"
        />
      </Flex>

      {/* Big status indicator */}
      <MotionBox
        bg={colors.bgCard}
        border="1px solid"
        borderColor={colors.border}
        borderRadius="2xl"
        p={8}
        textAlign="center"
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.4 }}
        boxShadow="none"
      >
        <Flex justifyContent="center" mb={4}>
          <Flex
            width="80px"
            height="80px"
            borderRadius="full"
            bg={`${resultadoGeneral.color}15`}
            border="3px solid"
            borderColor={`${resultadoGeneral.color}40`}
            alignItems="center"
            justifyContent="center"
          >
            <ResultIcon size={36} color={resultadoGeneral.color} />
          </Flex>
        </Flex>
        <Text
          tid={resultadoGeneral.tid}
          fontSize="xl"
          fontWeight="bold"
          color={resultadoGeneral.color}
        />

        {/* Summary counts */}
        <Grid templateColumns="repeat(3, 1fr)" gap={4} mt={6}>
          <Box>
            <Text fontSize="2xl" fontWeight="bold" color="#2a8a87">
              {resultado.totalConformes}
            </Text>
            <Text
              tid="recepciones.resultadoPage.summary.conformes"
              fontSize="xs"
              color={colors.textMuted}
              textTransform="uppercase"
              fontWeight="600"
            />
          </Box>
          <Box>
            <Text fontSize="2xl" fontWeight="bold" color="#EAB308">
              {resultado.totalObservados}
            </Text>
            <Text
              tid="recepciones.resultadoPage.summary.observados"
              fontSize="xs"
              color={colors.textMuted}
              textTransform="uppercase"
              fontWeight="600"
            />
          </Box>
          <Box>
            <Text fontSize="2xl" fontWeight="bold" color="#c8442f">
              {resultado.totalNoConformes}
            </Text>
            <Text
              tid="recepciones.resultadoPage.summary.noConformes"
              fontSize="xs"
              color={colors.textMuted}
              textTransform="uppercase"
              fontWeight="600"
            />
          </Box>
        </Grid>
      </MotionBox>

      {/* Validations list */}
      <MotionBox
        bg={colors.bgCard}
        border="1px solid"
        borderColor={colors.border}
        borderRadius="2xl"
        overflow="hidden"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.15 }}
        boxShadow="none"
      >
        <Box p={5} borderBottom="1px solid" borderColor={colors.border}>
          <Text
            tid="recepciones.resultadoPage.detailsTitle"
            fontSize="md"
            fontWeight="bold"
            color={colors.text}
          />
        </Box>

        <VStack gap={0} alignItems="stretch">
          {resultado.validaciones.map((val) => (
            <ValidationRow
              key={val.id}
              validation={val}
              isExpanded={expandedId === val.id}
              onToggle={() => setExpandedId(expandedId === val.id ? null : val.id)}
              colors={colors}
              onStartManual={() =>
                setManualForm({
                  validacionId: val.id,
                  resultado: ResultadoValidacion.Conforme,
                  observaciones: '',
                })
              }
              manualForm={manualForm?.validacionId === val.id ? manualForm : null}
              onManualResultadoChange={(r) =>
                setManualForm((prev) => (prev ? { ...prev, resultado: r } : null))
              }
              onManualObsChange={(obs) =>
                setManualForm((prev) => (prev ? { ...prev, observaciones: obs } : null))
              }
              onSaveManual={handleSaveManual}
              onCancelManual={() => setManualForm(null)}
              savingManual={savingManual}
            />
          ))}
        </VStack>
      </MotionBox>

      {/* Action buttons */}
      {!hasPendientes && (
        <Flex gap={3} justifyContent="center" pb={6}>
          {!hasNoConformes && (
            <Box
              as="button"
              px={8}
              py={3}
              bg="#2a8a87"
              color="white"
              borderRadius="lg"
              fontSize="sm"
              fontWeight="600"
              cursor="pointer"
              _hover={{ opacity: 0.9 }}
              transition="all 0.2s"
              onClick={() => setShowConfirmDialog('aprobar')}
            >
              {t('recepciones.resultadoPage.aceptarButton')}
            </Box>
          )}
          <Box
            as="button"
            px={8}
            py={3}
            bg="#c8442f"
            color="white"
            borderRadius="lg"
            fontSize="sm"
            fontWeight="600"
            cursor="pointer"
            _hover={{ opacity: 0.9 }}
            transition="all 0.2s"
            onClick={() => setShowConfirmDialog('rechazar')}
          >
            {t('recepciones.resultadoPage.rechazarButton')}
          </Box>
        </Flex>
      )}

      {/* Confirmation Dialog */}
      <AnimatePresence>
        {showConfirmDialog && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 100,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'rgba(0,0,0,0.5)',
            }}
            onClick={() => setShowConfirmDialog(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
              style={{
                background: 'white',
                borderRadius: '16px',
                padding: '24px',
                maxWidth: '440px',
                width: '90%',
              }}
            >
              <Text
                tid={
                  showConfirmDialog === 'aprobar'
                    ? 'recepciones.resultadoPage.dialog.aprobarTitle'
                    : 'recepciones.resultadoPage.dialog.rechazarTitle'
                }
                fontSize="lg"
                fontWeight="bold"
                color={colors.text}
                mb={2}
              />
              <Text
                tid={
                  showConfirmDialog === 'aprobar'
                    ? 'recepciones.resultadoPage.dialog.aprobarSubtitle'
                    : 'recepciones.resultadoPage.dialog.rechazarSubtitle'
                }
                fontSize="sm"
                color={colors.textSecondary}
                mb={4}
              />

              <Box mb={4}>
                <Text
                  tid="recepciones.resultadoPage.dialog.observacionesLabel"
                  fontSize="xs"
                  fontWeight="600"
                  color={colors.textMuted}
                  mb={1}
                />
                <Box
                  as="textarea"
                  width="100%"
                  rows={3}
                  p={3}
                  borderRadius="lg"
                  border="1px solid"
                  borderColor={colors.border}
                  fontSize="sm"
                  color={colors.text}
                  bg={colors.bgCard}
                  value={observaciones}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                    setObservaciones(e.target.value)
                  }
                  placeholder={t('recepciones.resultadoPage.dialog.observacionesPlaceholder')}
                />
              </Box>

              <Flex gap={3} justifyContent="flex-end">
                <Box
                  as="button"
                  px={4}
                  py={2}
                  borderRadius="lg"
                  fontSize="sm"
                  fontWeight="500"
                  cursor="pointer"
                  color={colors.textSecondary}
                  _hover={{ bg: colors.bgHover }}
                  onClick={() => setShowConfirmDialog(null)}
                >
                  {t('recepciones.resultadoPage.dialog.cancelar')}
                </Box>
                <Box
                  as="button"
                  px={4}
                  py={2}
                  borderRadius="lg"
                  fontSize="sm"
                  fontWeight="600"
                  cursor="pointer"
                  color="white"
                  bg={showConfirmDialog === 'aprobar' ? '#2a8a87' : '#c8442f'}
                  _hover={{ opacity: 0.9 }}
                  opacity={finalizando ? 0.7 : 1}
                  onClick={() => handleFinalizar(showConfirmDialog === 'aprobar')}
                >
                  {finalizando
                    ? t('recepciones.resultadoPage.dialog.procesando')
                    : showConfirmDialog === 'aprobar'
                      ? t('recepciones.resultadoPage.dialog.confirmarAprobacion')
                      : t('recepciones.resultadoPage.dialog.confirmarRechazo')}
                </Box>
              </Flex>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </VStack>
  )
}

// ── Validation Row ──

interface ValidationRowProps {
  validation: ResultadoValidacionItem
  isExpanded: boolean
  onToggle: () => void
  colors: typeof themeColors
  onStartManual: () => void
  manualForm: { resultado: ResultadoValidacion; observaciones: string } | null
  onManualResultadoChange: (r: ResultadoValidacion) => void
  onManualObsChange: (obs: string) => void
  onSaveManual: () => void
  onCancelManual: () => void
  savingManual: boolean
}

function ValidationRow({
  validation,
  isExpanded,
  onToggle,
  colors,
  onStartManual,
  manualForm,
  onManualResultadoChange,
  onManualObsChange,
  onSaveManual,
  onCancelManual,
  savingManual,
}: ValidationRowProps) {
  const { t } = useT()
  const resultInfo =
    validation.resultado !== null && validation.resultado !== undefined
      ? RESULTADO_CONFIG[validation.resultado]
      : null

  const iconColor = resultInfo?.color ?? '#857b71'
  const Icon = resultInfo?.icon ?? LuTriangleAlert

  const tipoLabel =
    validation.tipo === TipoValidacion.Automatica
      ? t('recepciones.resultadoPage.tipoAutomatica')
      : t('recepciones.resultadoPage.tipoManual')

  return (
    <Box borderBottom="1px solid" borderColor={colors.border}>
      <Flex
        as="button"
        width="100%"
        alignItems="center"
        gap={3}
        px={5}
        py={4}
        cursor="pointer"
        _hover={{ bg: colors.bgHover }}
        transition="all 0.15s"
        onClick={onToggle}
      >
        <Flex
          width="28px"
          height="28px"
          borderRadius="full"
          bg={`${iconColor}15`}
          alignItems="center"
          justifyContent="center"
          flexShrink={0}
        >
          <Icon size={14} color={iconColor} />
        </Flex>

        <Box flex={1} textAlign="left">
          <Text fontSize="sm" fontWeight="600" color={colors.text}>
            {validation.nombre}
          </Text>
          {validation.mensaje && (
            <Text fontSize="xs" color={colors.textMuted} mt={0.5}>
              {validation.mensaje}
            </Text>
          )}
        </Box>

        {validation.pendiente && (
          <Box
            px={2}
            py={0.5}
            borderRadius="full"
            fontSize="10px"
            fontWeight="700"
            bg="#857b7120"
            color="#857b71"
          >
            {t('recepciones.resultadoPage.pendienteLabel')}
          </Box>
        )}

        {resultInfo && (
          <Box
            px={2}
            py={0.5}
            borderRadius="full"
            fontSize="10px"
            fontWeight="700"
            bg={`${iconColor}20`}
            color={iconColor}
          >
            {t(resultInfo.tid).toUpperCase()}
          </Box>
        )}

        <Box
          transform={isExpanded ? 'rotate(180deg)' : 'rotate(0deg)'}
          transition="transform 0.2s"
          color={colors.textMuted}
        >
          <LuChevronDown size={16} />
        </Box>
      </Flex>

      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            style={{ overflow: 'hidden' }}
          >
            <Box px={5} pb={4} pl={16}>
              {validation.detalles && (
                <Box mb={3}>
                  <Text
                    tid="recepciones.resultadoPage.detallesLabel"
                    fontSize="xs"
                    fontWeight="600"
                    color={colors.textMuted}
                    mb={1}
                  />
                  <Text fontSize="sm" color={colors.textSecondary}>
                    {validation.detalles}
                  </Text>
                </Box>
              )}

              <Text
                tid="recepciones.resultadoPage.tipoLabel"
                values={{ tipo: tipoLabel }}
                fontSize="xs"
                color={colors.textMuted}
              />

              {/* Manual validation form */}
              {validation.pendiente &&
                validation.tipo === TipoValidacion.Manual &&
                !manualForm && (
                  <Box mt={3}>
                    <Box
                      as="button"
                      px={3}
                      py={1.5}
                      fontSize="xs"
                      fontWeight="600"
                      bg={brand.primary}
                      color="white"
                      borderRadius="md"
                      cursor="pointer"
                      _hover={{ opacity: 0.9 }}
                      onClick={(e: React.MouseEvent) => {
                        e.stopPropagation()
                        onStartManual()
                      }}
                    >
                      {t('recepciones.resultadoPage.evaluarManual')}
                    </Box>
                  </Box>
                )}

              {manualForm && (
                <Box mt={3} p={3} bg={colors.bgHover} borderRadius="lg">
                  <Text
                    tid="recepciones.resultadoPage.evaluacionManualTitle"
                    fontSize="xs"
                    fontWeight="600"
                    color={colors.textMuted}
                    mb={2}
                  />

                  <Flex gap={2} mb={3}>
                    {[ResultadoValidacion.Conforme, ResultadoValidacion.NoConforme].map(
                      (r) => {
                        const cfg = RESULTADO_CONFIG[r]
                        const selected = manualForm.resultado === r
                        return (
                          <Box
                            key={r}
                            as="button"
                            px={3}
                            py={1.5}
                            fontSize="xs"
                            fontWeight="600"
                            borderRadius="md"
                            cursor="pointer"
                            bg={selected ? `${cfg.color}20` : 'transparent'}
                            color={selected ? cfg.color : colors.textMuted}
                            border="1px solid"
                            borderColor={selected ? cfg.color : colors.border}
                            _hover={{ bg: `${cfg.color}10` }}
                            onClick={(e: React.MouseEvent) => {
                              e.stopPropagation()
                              onManualResultadoChange(r)
                            }}
                          >
                            {t(cfg.tid)}
                          </Box>
                        )
                      },
                    )}
                  </Flex>

                  <Input
                    placeholder={t('recepciones.resultadoPage.observacionesPlaceholder')}
                    size="sm"
                    value={manualForm.observaciones}
                    onChange={(e) => onManualObsChange(e.target.value.slice(0, 2000))}
                    onClick={(e: React.MouseEvent) => e.stopPropagation()}
                    mb={1}
                    maxLength={2000}
                  />
                  <Box textAlign="right" mb={3}>
                    <Text fontSize="xs" color={colors.textMuted} fontVariantNumeric="tabular-nums">
                      {manualForm.observaciones.length}/2000
                    </Text>
                  </Box>

                  <Flex gap={2} justifyContent="flex-end">
                    <Box
                      as="button"
                      px={3}
                      py={1.5}
                      fontSize="xs"
                      color={colors.textMuted}
                      cursor="pointer"
                      onClick={(e: React.MouseEvent) => {
                        e.stopPropagation()
                        onCancelManual()
                      }}
                    >
                      {t('recepciones.resultadoPage.cancelarButton')}
                    </Box>
                    <Box
                      as="button"
                      px={3}
                      py={1.5}
                      fontSize="xs"
                      fontWeight="600"
                      bg={brand.primary}
                      color="white"
                      borderRadius="md"
                      cursor="pointer"
                      opacity={savingManual ? 0.7 : 1}
                      _hover={{ opacity: 0.9 }}
                      onClick={(e: React.MouseEvent) => {
                        e.stopPropagation()
                        onSaveManual()
                      }}
                    >
                      {savingManual
                        ? t('recepciones.resultadoPage.guardandoButton')
                        : t('recepciones.resultadoPage.guardarButton')}
                    </Box>
                  </Flex>
                </Box>
              )}
            </Box>
          </motion.div>
        )}
      </AnimatePresence>
    </Box>
  )
}
